import { useCallback, createContext, useContext, useRef, ReactNode, useSyncExternalStore } from "react";
import { CustomerDetailsType, CustomerPreferredLanguageType, LastAppStateType } from "./sharedContextTypes/userTypes";
import { apiPOST, apiPOSTnew, apiGET } from "../utils/apiRequests";
import { setCookieWithDomain, getCookieValue, deleteCookie } from "../utils/cookieUtils";
import { HOUR_IN_SECONDS } from "../utils/consts/applicationConstants";
import { decode } from "html-entities";


//---------------<Data types>------------------------------------------------------------------------------------------
// never got an error in this style, they are strings most of the time
type GetConsentCategoriesErrorsType = {
	code: number;
	description: string;
	message: string;
};

type GetConsentCategoriesResultSourcesType = {
	crm: boolean;			// manually created in the application
	import: boolean;		// imported from the import wizard
	page: boolean;			// tracked from the consent page
	private_api: boolean;	// API which uses basic authentication
	public_api: boolean;	// API, which uses only public token for authentication
	scenario: boolean;		// tracked from the scenario from event node
};

type GetConsentCategoriesResultTranslationsType = {
	"en"?: {
		description: string;
		name: string;
	},
	"nl"?: {
		description: string;
		name: string;
	},
	""?: {
		description: string;
		name: string;
	}
};

type GetConsentCategoriesResultType = {
	id: string;	// name of consent category
	legitimate_interest: boolean;
	sources: GetConsentCategoriesResultSourcesType;
	translations: GetConsentCategoriesResultTranslationsType;
};

type GetConsentCategoriesCallReturnType = {
	results?: GetConsentCategoriesResultType[];
	errors?: string[];
	// errors?: GetConsentCategoriesErrorsType; // => we never get errors in this format from BR, but we also done because we check !result.ok
	success?: boolean;
};

type GetConsentCategoryAttributesType = {
	type: "consent";
	category: string;
	mode: 
		"message" |		// Returns a value from the message attribute for the last consent event category.
		"source" |		// Returns a source for the last consent event.
		"timestamp" |	// Returns a timestamp for the last consent event as a UNIX time.
		"valid" |		// Default value. Returns whether consent is valid or not (true/false).
		"until";		// Returns a value from valid_until attribute for the last consent event.
};

type SetIndividualConsentEventPropertiesType = {
	action: "accept" | "reject";
	category: string;
	valid_until: "unlimited";
	message: "This consent change was tracked from the members portal."
};

type SetConsentBatchCommandType = {
	name: "customers/events";
	command_id: string;
	data: {
		customer_ids: {
			client_id: number
		};
		event_type: "consent";
		timestamp: number;
		properties: SetIndividualConsentEventPropertiesType;
	}
}

type SetConsentsCallReturnType = {
	results?: {
		success: boolean;
		errors?: string;
		command_id: string;
	}[];
	start_time?: number;
	end_time?: number;
	success?: boolean;
	error?: string;
};

type GetCustomerConsentsResultType = {
	success?: boolean;
	value?: boolean;
	error?: string;
};

type GetCustomerConsentsCallReturnType = {
	results?: GetCustomerConsentsResultType[];
	errors?: string[];
	// errors?: GetConsentCategoriesErrorsType; // => we never get errors in this format from BR, but we also done because we check !result.ok
	success?: boolean;
};

export type CustomerConsentType = {
	id: string;	// name of consent category
	legitimate_interest: boolean;
	translations: GetConsentCategoriesResultTranslationsType;
} & GetCustomerConsentsResultType;

export type UserDataType = {
	status?: boolean;
	token?: string;
	customerDetails?: CustomerDetailsType;
	customerConsents?: CustomerConsentType[];
	errors?: string[];
	// UI values ----------------------------
	customerConsentsUIvalue?: CustomerConsentType[];
};

export type AuthStateType = "not-logged-in" | "guest" | "authenticated-user" | "undefined";

export type UserAuthStateAndDataType = UserDataType & {
	authState: AuthStateType;
	setByLogin?: boolean;
	lastAppState? :LastAppStateType;
	backButtonURL?: string;
};

export type UpdateCustomerPreferredLanguageCallReturnType = {
	status?: boolean;
	errors?: string[];
};

//---------------</Data types>-----------------------------------------------------------------------------------------

let fetchLoginController: AbortController;
let fetchLogoutController: AbortController;

//---------------<User Auth State manager>-----------------------------------------------------------------------------

function useUserAuthData(): {
	get: () => UserAuthStateAndDataType;
	set: (value: Partial<UserAuthStateAndDataType>) => void; // so we can set individual properties of the object
	subscribe: (callback: () => void) => () => void;
} {
	// the init value is set inside, because the auth context should only initialize 1x
	// this adds to simplicity of use and there is no benefit to exposing it as a property
	const user = useRef<UserAuthStateAndDataType>({
		authState: "undefined"
	});

	// we use set, because we only want a single type of a function being added once
	const subscribers = useRef(new Set<()=> void>()); 

	const get = useCallback(() => user.current, []);
	const set = useCallback((value: Partial<UserAuthStateAndDataType>) => {
		user.current = { ...user.current, ...value};
		subscribers.current.forEach((callback) => callback());
	}, []);

	// subscriber is added so we can subscribe to any value changes to the state (in our case of the "user") with useSyncExternalStore
	// when we add a subscriber we return the delete function of that subscriber
	const subscribe = useCallback((callback: () => void) => {
		subscribers.current.add(callback);
		return () => subscribers.current.delete(callback);
	}, []);

	return { get, set, subscribe };
}

type UseUserAuthDataReturnType = ReturnType<typeof useUserAuthData>;

const UserAuthContext = createContext<UseUserAuthDataReturnType | null>(null);

export function UserAuthContextProvider({ children }: { children: ReactNode }) {
	const userAuthData = useUserAuthData();
	return (
		<UserAuthContext.Provider value={userAuthData}>
			{children}
		</UserAuthContext.Provider>
	);
}

// SelectorOutput will catch the type of the selected value
// selector will rerender only components that register it
export function useUserAuth<SelectorOutput>(
	selector: (user: UserAuthStateAndDataType) => SelectorOutput 
): [ SelectorOutput, (value: Partial<UserAuthStateAndDataType>) => void] {
	const userAuthContext = useContext(UserAuthContext);
	if (!userAuthContext) {
		throw new Error("UserAuthContext not found!");
	}
	const state = useSyncExternalStore(userAuthContext.subscribe, () => selector(userAuthContext.get()));
	return [state, userAuthContext.set]
}

//---------------</User Auth State manager>----------------------------------------------------------------------------
//---------------<Session data functions>------------------------------------------------------------------------------

export async function updateCustomerPreferredLanguage(rootApiEndpointUrl: string, userID: number, authToken: string, language: CustomerPreferredLanguageType | undefined, abortControllerSignal?: AbortSignal | null | undefined): Promise<UpdateCustomerPreferredLanguageCallReturnType> {
	let updateCustomerPreferredLanguageData: UpdateCustomerPreferredLanguageCallReturnType = {};

	const formData = new FormData();
	formData.append("token", authToken);
	formData.append("pageParams[customerID]", String(userID));
	formData.append("pageParams[customerPreferredLanguage]", language ? language : "en");

	const updateCustomerPreferredLanguageCallResult = await apiPOST(`${rootApiEndpointUrl}${import.meta.env.REACT_APP_API_UPDATE_CUSTOMER_PREFERRED_LANGUAGE}`, formData, abortControllerSignal);

	if (typeof updateCustomerPreferredLanguageCallResult === "object" && updateCustomerPreferredLanguageCallResult !== null) {
		updateCustomerPreferredLanguageData = updateCustomerPreferredLanguageCallResult as UpdateCustomerPreferredLanguageCallReturnType;
	}
	else if (typeof updateCustomerPreferredLanguageCallResult === "string") {
		updateCustomerPreferredLanguageData = { errors: ["Change language data error: " + updateCustomerPreferredLanguageCallResult] };
	}
	/*
	this will happen when we abort a fetch because we set it to return null
	else {
		const error = "Change language failed without errors!";
		updateCustomerPreferredLanguageData = { errors: [error] };
	}
	*/
	return updateCustomerPreferredLanguageData;
}

export async function getConsentCategories(
	abortControllerSignal?: AbortSignal | null
): Promise<Partial<GetConsentCategoriesCallReturnType>> {

	let getConsentCategoriesData: Partial<GetConsentCategoriesCallReturnType> = {};
	const getConsentCategoriesHeaders = new Headers({
		authorization: `Basic ${import.meta.env.REACT_APP_BLOOMREACH_CONSENT_API_AUTH}`,
		"content-type": "application/json",
		accept: "application/json"
	});
	
	const getConsentCategoriesCallResult = await apiGET(`${import.meta.env.REACT_APP_BLOOMREACH_API_URL}/data/v2/projects/${import.meta.env.REACT_APP_BLOOMREACH_TOKEN}/consent/categories`, getConsentCategoriesHeaders, abortControllerSignal);

	if (typeof getConsentCategoriesCallResult === "object" && getConsentCategoriesCallResult !== null) {
		getConsentCategoriesData = getConsentCategoriesCallResult as GetConsentCategoriesCallReturnType;
	}
	else if (typeof getConsentCategoriesCallResult === "string") {
		getConsentCategoriesData = { errors: [getConsentCategoriesCallResult] };
	}
	
	// this will happen when we abort a fetch because we set it to return null
	else {
		const error = "Get categories failed without errors!";
		getConsentCategoriesData = { errors: [error] };
	}
	
	return getConsentCategoriesData;
}

export async function getCustomerConsents(
	clientID: number,
	categories: GetConsentCategoriesResultType[],
	abortControllerSignal?: AbortSignal | null
): Promise<GetCustomerConsentsCallReturnType> {
	let getCustomerConsentsData: Partial<GetCustomerConsentsCallReturnType> = {};
	const getConsentCategoriesHeaders = new Headers({
		authorization: `Basic ${import.meta.env.REACT_APP_BLOOMREACH_CONSENT_API_AUTH}`,
		"content-type": "application/json",
		accept: "application/json"
	});
	const consentCategoriesAttributes: GetConsentCategoryAttributesType[] = [];
	for (const category of categories) {
		consentCategoriesAttributes.push( 
			{
				type: "consent",
				category: category.id,
				mode: "valid"
			}
		)
	}
	const getCustomerConsentsBody = JSON.stringify({
		customer_ids: {
			client_id: clientID
		},
		attributes: consentCategoriesAttributes
	});
	const getCustomerConsentsCallResult = await apiPOSTnew(`${import.meta.env.REACT_APP_BLOOMREACH_API_URL}/data/v2/projects/${import.meta.env.REACT_APP_BLOOMREACH_TOKEN}/customers/attributes`, getConsentCategoriesHeaders, getCustomerConsentsBody, abortControllerSignal);

	if (typeof getCustomerConsentsCallResult === "object" && getCustomerConsentsCallResult !== null) {
		getCustomerConsentsData = getCustomerConsentsCallResult as GetCustomerConsentsCallReturnType;
	}
	else if (typeof getCustomerConsentsCallResult === "string") {
		getCustomerConsentsData = { errors: [getCustomerConsentsCallResult] };
	}
	// this will happen when we abort a fetch because we set it to return null
	else {
		const error = "Get customer consent data failed without errors!";
		getCustomerConsentsData = { errors: [error] };
	}
	
	return getCustomerConsentsData;
}

export async function setCustomerConsents(
	clientID: number,
	customerConsents: CustomerConsentType[],
	abortControllerSignal?: AbortSignal | null
): Promise<SetConsentsCallReturnType> {

	let setCustomerConsentsData: Partial<SetConsentsCallReturnType> = {};
	const setCustomerConsentHeaders = new Headers({
		authorization: `Basic ${import.meta.env.REACT_APP_BLOOMREACH_CONSENT_API_AUTH}`,
		"content-type": "application/json",
		accept: "application/json"
	});

	const setBatchConsentEventCommands: SetConsentBatchCommandType[] = [];
	const date = new Date();
	for (const customerConsent of customerConsents) {
		setBatchConsentEventCommands.push(
			{
				name: "customers/events",
				command_id: customerConsent.id,
				data: {
					customer_ids: {
						client_id: clientID
					},
					event_type: "consent",
					timestamp: Math.round(date.getTime() / 1000),
					properties: {
						action: customerConsent.value ? "accept" : "reject",
						category: customerConsent.id,
						valid_until: "unlimited",
						message: "This consent change was tracked from the members portal."
					}
				}
			}
		);
	}
	
	const setCustomerConsentsBody = JSON.stringify({
		commands: setBatchConsentEventCommands
	});
	const setCustomerConsentsCallResult = await apiPOSTnew(`${import.meta.env.REACT_APP_BLOOMREACH_API_URL}/track/v2/projects/${import.meta.env.REACT_APP_BLOOMREACH_TOKEN}/batch`, setCustomerConsentHeaders, setCustomerConsentsBody, abortControllerSignal);

	if (typeof setCustomerConsentsCallResult === "object" && setCustomerConsentsCallResult !== null) {
		setCustomerConsentsData = setCustomerConsentsCallResult as SetConsentsCallReturnType;
	}
	else if (typeof setCustomerConsentsCallResult === "string") {
		setCustomerConsentsData = { error: setCustomerConsentsCallResult };
	}
	// this will happen when we abort a fetch because we set it to return null
	else {
		const error = "Set customer consent failed without errors!";
		setCustomerConsentsData = { error: error };
	}
	
	return setCustomerConsentsData;
}

// Use the return of this function to update user auth data, via the setter exposed by useUserAuth. (note:remember to use selector properly)
export async function continueSession(): Promise<UserAuthStateAndDataType> {
	let user: UserAuthStateAndDataType = { 
		authState: "not-logged-in"
	};
	const userStringValue = await getCookieValue(import.meta.env.REACT_APP_USER_COOKIE);
	const userTokenStringValue = await getCookieValue(import.meta.env.REACT_APP_USER_TOKEN);
	const lastAppStateString = import.meta.env.REACT_APP_LOCAL_STORAGE_LAST_APP_STATE ? 
		localStorage.getItem(import.meta.env.REACT_APP_LOCAL_STORAGE_LAST_APP_STATE) : "";
	const lastAppState = lastAppStateString ? JSON.parse(lastAppStateString) as LastAppStateType : {};
	lastAppState.selectedLanguage = (import.meta.env.REACT_APP_ENV === "dist-eu" || import.meta.env.REACT_APP_ENV === "checkout-dist-eu") ? lastAppState.selectedLanguage : "en";
	
	if (userStringValue && userTokenStringValue) {
		const userData = JSON.parse(userStringValue) as UserDataType;
		if (userData && userData.status && userData.token && !userData.errors) {
			user = {
				...userData,
				lastAppState: lastAppState,
				authState: "authenticated-user"
			};
		}
		else {
			// this will never happen because we don't save invalid responses and errors in cookies
			user = {
				...userData,
				lastAppState: lastAppState,
				authState: "not-logged-in"
			};
		}
	}
	else {
		user = {
			lastAppState: lastAppState,
			authState: "not-logged-in"
		};
	}
	return user;
}

// Use the return of this function to update user auth data, via the setter exposed by useUserAuth. (note:remember to use selector properly)
export async function logIn(rootApiEndpointUrl: string, username: string, password: string): Promise<UserAuthStateAndDataType> {
	if (fetchLoginController) {
		fetchLoginController.abort();
	}
	fetchLoginController = new AbortController();

	let user: UserAuthStateAndDataType = { 
		authState: "not-logged-in"
	};
	let userData: UserDataType = {};
	const formData = new FormData();
	formData.append("pageParams[username]", username);
	formData.append("pageParams[password]", password);
	// TODO re architect this call to be the same format as all others
	const result = await apiPOST(`${rootApiEndpointUrl}${import.meta.env.REACT_APP_API_GET_AUTH_TOKEN}`, formData, fetchLoginController.signal);
	if (typeof result === "object") {
		userData = result as UserDataType;
		if (userData.status && userData.customerDetails && !userData.errors && userData.token) {
			userData.customerDetails.customerEmail = decode(userData.customerDetails.customerEmail);
			userData.customerDetails.customerName = decode(userData.customerDetails.customerName);
			userData.customerDetails.customerSurName = decode(userData.customerDetails.customerSurName);
			setCookieWithDomain(import.meta.env.REACT_APP_USER_TOKEN, userData.token, HOUR_IN_SECONDS, import.meta.env.REACT_APP_USER_TOKEN_DOMAIN);
			user = {
				authState: "authenticated-user",
				status: userData.status,
				token: userData.token,
				customerDetails: userData.customerDetails,
				errors: userData.errors,
				setByLogin: true
			};
			return user;
		}
	}
	else if (typeof result === "string") {
		userData = { errors: [result] };
	}
	/*
	this will happen when we abort a fetch because we set it to return null
	else {
		// Capt. Bertorelli says: is not supposed to happen! What-a are we gonna do?
		const error = "Login failed without errors!";
		userData = { errors: [error] };
	}
	*/
	deleteCookie(import.meta.env.REACT_APP_USER_COOKIE);
	deleteCookie(import.meta.env.REACT_APP_USER_TOKEN);
	user = {
		authState: "not-logged-in",
		status: userData.status,
		token: userData.token,
		customerDetails: userData.customerDetails,
		errors: userData.errors
	};
	return user;
}

// Use the return of this function to update user auth data, via the setter exposed by useUserAuth. (note:remember to use selector properly)
export async function logOut(rootApiEndpointUrl: string, user: UserAuthStateAndDataType): Promise<UserAuthStateAndDataType> {
	if (fetchLogoutController) {
		fetchLogoutController.abort();
	}
	fetchLogoutController = new AbortController();
	
	if (
		user && user.status && 
		user.token && user.customerDetails && 
		user.customerDetails.customerID
	) {  
		const formData = new FormData();
		formData.append("token", user.token);
		formData.append("pageParams[customerID]", user.customerDetails.customerID + "");
		await apiPOST(`${rootApiEndpointUrl}${import.meta.env.REACT_APP_API_LOGOUT}`, formData, fetchLogoutController.signal);
	}
	
	// clear user info and user token cookies
	deleteCookie(import.meta.env.REACT_APP_USER_COOKIE);
	deleteCookie(import.meta.env.REACT_APP_USER_TOKEN);
	const clearUser = {
		...user,
		authState: "not-logged-in"
	} as UserAuthStateAndDataType;
	return clearUser;
}

//---------------</Session data functions>-----------------------------------------------------------------------------
